import socketService from "./chatSocketService";
import apiClient from "./apiClient";
import { auth } from "./api";

/**
 * Get conversation for a specific ticket using REST API
 * Backend doesn't handle this via socket
 */
export const getConversation = async (ticketId, page = 1, limit = 50) => {
  try {
    const response = await apiClient.get(`/chat/conversation/${ticketId}`, {
      params: { page, limit },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching conversation:", error);
    throw error;
  }
};

/**
 * Send a message using socket
 */
export const sendMessage = (messageData) => {
  return new Promise((resolve, reject) => {
    try {
      // Send message via socket - backend handles this with chat-message event
      socketService.sendMessage(messageData);

      // Since backend doesn't send confirmation, just resolve immediately
      // The actual message will be received via the normal message listener
      resolve({ success: true, message: messageData });
    } catch (error) {
      console.error("Error sending message:", error);
      reject(error);
    }
  });
};

/**
 * Mark messages as read using socket
 */
export const markMessagesAsRead = (ticketId, messageIds = []) => {
  return new Promise((resolve, reject) => {
    try {
      socketService.markAsRead(ticketId, messageIds);
      resolve({ success: true });
    } catch (error) {
      console.error("Error marking messages as read:", error);
      reject(error);
    }
  });
};

/**
 * Get user conversations using REST API
 * Backend doesn't handle this via socket
 */
export const getUserConversations = async (page = 1, limit = 20) => {
  try {
    const response = await apiClient.get("/chat/conversations", {
      params: { page, limit },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching conversations:", error);
    throw error;
  }
};

/**
 * Send voice call invitation using socket
 */
export const sendCallInvite = (ticketId, callData) => {
  return new Promise((resolve, reject) => {
    try {
      const data = { ticketId, ...callData };
      socketService.sendVoiceCallInvite(data);
      resolve({ success: true, data });
    } catch (error) {
      console.error("Error sending call invite:", error);
      reject(error);
    }
  });
};

/**
 * Respond to call invitation using socket
 */
export const respondToCall = (callId, response, ticketId) => {
  return new Promise((resolve, reject) => {
    try {
      const data = { callId, response, ticketId };
      socketService.respondToVoiceCall(data);
      resolve({ success: true, data });
    } catch (error) {
      console.error("Error responding to call:", error);
      reject(error);
    }
  });
};

/**
 * Upload file for chat using REST API
 * Backend doesn't handle file upload via socket
 */
export const uploadFile = async (file, ticketId) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("ticketId", ticketId);

    // Don't set Content-Type for FormData - let browser set it with boundary
    // apiClient will handle authorization header
    const response = await apiClient.post("/chat/upload", formData);
    return response.data;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

/**
 * Get chat-enabled tickets using REST API
 * Backend doesn't handle this via socket
 */
export const getChatEnabledTickets = async (page = 1, limit = 20) => {
  try {
    const response = await apiClient.get("/ticket/chat", {
      params: { page, limit },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching chat-enabled tickets:", error);
    throw error;
  }
};

/**
 * Create new ticket using REST API
 * Backend doesn't handle this via socket
 */
export const createNewTicket = async (ticketData) => {
  try {
    const response = await apiClient.post("/ticket", ticketData);

    if (!response.data) {
      throw new Error("Failed to create ticket");
    }

    return response.data;
  } catch (error) {
    console.error("Error creating ticket:", error);
    throw error;
  }
};

/**
 * Initialize chat connection
 */
export const initializeChatConnection = () => {
  return new Promise((resolve, reject) => {
    try {
      // Connect to socket service
      socketService.connect();

      // Set up connection event listeners
      socketService.onMessage((message) => {
        console.log("New message received:", message);
      });

      socketService.onTyping((data) => {
        console.log("User typing:", data);
      });

      socketService.onVoiceCallInvite((data) => {
        console.log("Voice call invite received:", data);
      });

      socketService.onVoiceCallResponse((data) => {
        console.log("Voice call response received:", data);
      });

      resolve();
    } catch (error) {
      console.error("Failed to initialize chat connection:", error);
      reject(error);
    }
  });
};

/**
 * Authenticate user with chat service
 */
export const authenticateChat = (userData) => {
  return new Promise((resolve, reject) => {
    try {
      const token = localStorage.getItem("authToken");
      const authData = {
        token,
        ...userData,
      };

      socketService.authenticate(authData);
      resolve();
    } catch (error) {
      console.error("Failed to authenticate with chat service:", error);
      reject(error);
    }
  });
};

/**
 * Join a chat room
 */
export const joinChatRoom = (ticketId, userId, role, name) => {
  return new Promise((resolve, reject) => {
    try {
      socketService.joinChat(ticketId, userId, role, name);
      resolve();
    } catch (error) {
      console.error("Failed to join chat room:", error);
      reject(error);
    }
  });
};

/**
 * Leave current chat room
 */
export const leaveChatRoom = () => {
  return new Promise((resolve, reject) => {
    try {
      socketService.leaveChat();
      resolve();
    } catch (error) {
      console.error("Failed to leave chat room:", error);
      reject(error);
    }
  });
};

export const checkIfChatCsAvailable = async (baseUrl, siteId) => {
  try {
    const response = await apiClient.get(`/chat-schedules/${siteId}`);

    return response;
  } catch (error) {
    console.error("Failed to get customer service schedule:", error);
    return [];
  }
};
